/** Navigation targets shared by the shell sidebar, topbar breadcrumb, and the command palette */

import {
  BarChart3,
  BookOpen,
  BookText,
  CreditCard,
  FileText,
  FolderKanban,
  Globe,
  HeartPulse,
  HardDrive,
  Home,
  Library,
  MessageCircleQuestion,
  Network,
  Settings,
  Terminal,
  type LucideIcon,
} from 'lucide-react'
import type { TranslationKey } from '../../i18n'

export interface NavItem {
  labelKey: TranslationKey
  to: string
  icon: LucideIcon
  /** Opens the external docs site in a new tab */
  external?: boolean
  /** Other path prefixes that belong to this item (e.g. review proposals live under Sources) */
  also?: string[]
}

/** Docs site for the fork; API and CLI references moved there from the in-app /docs pages */
export const DOCS_URL = (import.meta.env.VITE_DOCS_URL ?? 'https://docs.vividkit.app').replace(/\/$/, '')

/** Docs page for the retired Secrets screen: why secrets moved to AgentKit's `ak secrets` and how to export them */
export const SECRETS_MOVED_DOCS_URL = `${DOCS_URL}/03-webapp-guide/secrets-moved-to-ak-secrets/`

export const projectPath = (slug: string, child = '') => `/p/${encodeURIComponent(slug)}${child ? `/${child}` : ''}`

/** The review queue ("Waiting for the wiki") is the second tab of the project's Sources screen */
export const projectReviewPath = (slug: string) => `${projectPath(slug, 'sources')}?tab=review`

/** Per-project pages (child routes of /p/:projectSlug) */
export function projectNavItems(slug: string): NavItem[] {
  return [
    { labelKey: 'nav.overview', to: projectPath(slug), icon: Home },
    { labelKey: 'nav.wiki', to: projectPath(slug, 'wiki'), icon: BookOpen },
    // The review queue is the second tab of Sources (?tab=review); proposal pages stay at review/:id
    { labelKey: 'nav.sources', to: projectPath(slug, 'sources'), icon: Library, also: [projectPath(slug, 'review')] },
    { labelKey: 'nav.ask', to: projectPath(slug, 'ask'), icon: MessageCircleQuestion },
    { labelKey: 'nav.health', to: projectPath(slug, 'health'), icon: HeartPulse },
  ]
}

/** Workspace pages; Graph is still a legacy screen. Secrets moved to AgentKit's `ak secrets` (/secrets explains) */
export const WORKSPACE_NAV_ITEMS: NavItem[] = [
  { labelKey: 'nav.documents', to: '/', icon: FileText },
  { labelKey: 'nav.allProjects', to: '/projects', icon: FolderKanban },
  { labelKey: 'nav.storage', to: '/storage', icon: HardDrive },
  { labelKey: 'nav.sites', to: '/sites', icon: Globe },
  { labelKey: 'nav.searchAnalytics', to: '/search-analytics', icon: BarChart3 },
  { labelKey: 'nav.graph', to: '/graph', icon: Network },
  { labelKey: 'nav.settings', to: '/settings', icon: Settings },
  { labelKey: 'nav.billing', to: '/billing', icon: CreditCard },
]

/** External documentation links (sidebar footer and command palette) */
export const DOCS_NAV_ITEMS: NavItem[] = [
  { labelKey: 'nav.apiDocs', to: `${DOCS_URL}/06-api-reference/`, icon: BookText, external: true },
  { labelKey: 'nav.cliDocs', to: `${DOCS_URL}/04-cli-reference/`, icon: Terminal, external: true },
]

function matchesPath(to: string, pathname: string): boolean {
  // Documents (/) also covers document pages at /doc/:slug
  if (to === '/') return pathname === '/' || pathname.startsWith('/doc/')
  return pathname === to || pathname.startsWith(`${to}/`)
}

/** The item whose path is the longest prefix of the current path (Overview vs Wiki, Settings vs Search analytics) */
export function activeNavTarget(items: NavItem[], pathname: string): string | undefined {
  return items
    .filter((item) => !item.external)
    .map((item) => ({ to: item.to, length: Math.max(-1, ...[item.to, ...(item.also ?? [])].filter((p) => matchesPath(p, pathname)).map((p) => p.length)) }))
    .filter((match) => match.length >= 0)
    .sort((a, b) => b.length - a.length)[0]?.to
}
